import React, { useId, useRef } from "react";

export const CODE_LENGTH = 8;

interface Props {
  hasError: boolean;
  onChange: (value: string) => void;
}

const selectInput = (input: React.MutableRefObject<HTMLInputElement | null>) =>
  input?.current?.select();

// With this small timeout, it will actually highlight any text in the input so you can immediately replace it
const selectAndHighlightInput = (input: React.MutableRefObject<HTMLInputElement | null>) => {
  setTimeout(() => selectInput(input), 0);
};

export const CodeInput = ({ hasError, onChange }: Props) => {
  const id = useId();
  const inputRefs = Array.from(new Array(CODE_LENGTH), () => useRef<HTMLInputElement | null>(null));

  const currentCode = () => inputRefs.map((input) => input.current?.value || "").join("");

  const handleKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    const currentInput = event.currentTarget as HTMLInputElement;
    const previousInputRef = inputRefs[index - 1];
    const nextInputRef = inputRefs[index + 1];

    if (event.key === "Backspace" && index > 0 && !currentInput.value) {
      selectInput(previousInputRef);
    } else if (event.key === "ArrowLeft" && index > 0) {
      selectAndHighlightInput(previousInputRef);
    } else if (event.key === "ArrowRight" && index < CODE_LENGTH - 1) {
      selectAndHighlightInput(nextInputRef);
    }
  };

  const handleChange = (index: number, event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    if (value.length > 1) {
      fillInputs(index, value);
      return;
    }

    onChange(currentCode());

    if (value && index < CODE_LENGTH - 1) {
      selectInput(inputRefs[index + 1]);
    }
  };

  const fillInputs = (startIndex: number, text: string) => {
    const characters = text.split("");

    characters.forEach((character, i) => {
      const inputIndex = startIndex + i;
      if (inputIndex >= CODE_LENGTH) {
        return;
      }

      inputRefs[inputIndex].current!.value = character;
    });

    onChange(currentCode());

    const lastInputIndex = Math.min(startIndex + characters.length - 1, CODE_LENGTH - 1);
    selectInput(inputRefs[lastInputIndex + 1]);
  };

  const handlePaste = (startIndex: number, event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();

    const clipboardData = event.clipboardData?.getData("text") || "";
    fillInputs(startIndex, clipboardData);
  };

  return (
    <fieldset id={id} data-provides="login-security-code">
      <div className="login-security-code__wrapper">
        {inputRefs.map((inputRef, index) => (
          <input
            key={index}
            id={`${id}-${index}`}
            data-test-id={`security-input-${index}`}
            type="text"
            inputMode="numeric"
            ref={inputRef}
            aria-invalid={hasError}
            aria-errormessage="security-code-error"
            aria-required={true}
            onChange={(e) => handleChange(index, e)}
            onPaste={(e) => handlePaste(index, e)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            autoFocus={index === 0}
            autoCapitalize="off"
          />
        ))}
      </div>
    </fieldset>
  );
};
